import { createORPCClient } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import type { RouterClient } from "@orpc/server";
import type { AppRouter } from "@alm/api-contracts";
import { apiHttpOrigin } from "@/lib/env";

// A tablet on a wall holds a device token (header `x-board-device`) that fixes one building and
// names no person; the API prefers it over a session cookie. Its own link so that header never
// rides on requests about a person, which it would re-aim at the tablet's building.

const DEVICE_KEY = "alm.board.device";

export interface BoardDeviceRegistration {
  token: string;
  deviceId: string;
  deviceName: string;
  facilityId: string;
  facilityName: string;
}

export function getBoardDevice(): BoardDeviceRegistration | null {
  // Unreadable storage or a bad payload reads as no registration.
  try {
    const raw = localStorage.getItem(DEVICE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BoardDeviceRegistration;
    if (!parsed?.token || !parsed?.facilityId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setBoardDevice(registration: BoardDeviceRegistration): void {
  try {
    localStorage.setItem(DEVICE_KEY, JSON.stringify(registration));
  } catch {
    // Private browsing: works until reload, then lands on setup.
  }
}

// Private: drop a registration through `forgetRevokedDevice`, which also tells the screen.
function clearBoardDevice(): void {
  try {
    localStorage.removeItem(DEVICE_KEY);
  } catch {
    // Nothing to remove.
  }
}

export const BOARD_DEVICE_REVOKED_EVENT = "board-device-revoked";

// Never ask this of the code check: a wrong quick code and a revoked device both come back
// UNAUTHORIZED, and a typo must not deregister the board.
export function isDeviceRevoked(error: unknown): boolean {
  if (!getBoardDevice()) return false;
  return (error as { code?: unknown } | null)?.code === "UNAUTHORIZED";
}

export function forgetRevokedDevice(): void {
  clearBoardDevice();
  window.dispatchEvent(new Event(BOARD_DEVICE_REVOKED_EVENT));
}

const link = new RPCLink({
  url: `${apiHttpOrigin}/rpc`,
  headers: () => {
    const device = getBoardDevice();
    return device ? { "x-board-device": device.token } : {};
  },
  fetch: (request, init) => globalThis.fetch(request, { ...init, credentials: "include" }),
});

const client: RouterClient<AppRouter> = createORPCClient(link);

export const board = client.frontDesk.board;

// Administrator session only, so a different router that never sees a device token.
export const boardDevices = client.frontDesk.boardDevices;

type Board = typeof board;

export type BoardState = Awaited<ReturnType<Board["state"]>>;
export type BoardCallRow = BoardState["attention"][number];
export type BoardTicket = BoardState["tickets"][number];
export type BoardTicketPriority = BoardTicket["priority"];
export type BoardClaim = BoardState["claims"][number];
export type BoardActivityEntry = BoardState["activity"][number];
export type BoardHandoff = BoardState["handoffs"][number];
export type BoardSmsThread = BoardState["sms"]["threads"][number];
export type BoardSmsMessage = Awaited<ReturnType<Board["smsThread"]>>[number];
export type BoardActorGrant = Awaited<ReturnType<Board["verifyActor"]>>;
export type BoardActorUser = BoardActorGrant["user"];

// oRPC returns `Date`, JSON returns strings: read through `at`, never `.getTime()`.
export type Timestamp = Date | string;

export const at = (value: Timestamp): number => new Date(value).getTime();
